import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Edit2, X, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

// ─── types ────────────────────────────────────────────────────────────────────

interface Pattern {
  id: string;
  patternType: "keyword" | "regex";
  pattern: string;
}

interface NlpEntity {
  id: string;
  name: string;
  domain: string;
  description: string;
  isActive: boolean;
  createdAt: string;
  patterns: Pattern[];
}

// ─── API ─────────────────────────────────────────────────────────────────────

const api = {
  list:          () => apiRequest("GET", "/api/nlp/entities").then(r => r.json()),
  create:        (body: object) => apiRequest("POST", "/api/nlp/entities", body),
  update:        (id: string, body: object) => apiRequest("PUT", `/api/nlp/entities/${id}`, body),
  delete:        (id: string) => apiRequest("DELETE", `/api/nlp/entities/${id}`),
  addPattern:    (id: string, body: object) => apiRequest("POST", `/api/nlp/entities/${id}/patterns`, body),
  removePattern: (id: string, pId: string) => apiRequest("DELETE", `/api/nlp/entities/${id}/patterns/${pId}`),
};

// ─── entity dialog ────────────────────────────────────────────────────────────

function EntityDialog({ open, onClose, entity }: { open: boolean; onClose: () => void; entity?: NlpEntity }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const editing = !!entity;

  const [form, setForm] = useState({
    name:        entity?.name ?? "",
    domain:      entity?.domain ?? "",
    description: entity?.description ?? "",
    isActive:    entity?.isActive ?? true,
  });

  const mutation = useMutation({
    mutationFn: async () => {
      const res = editing
        ? await api.update(entity!.id, form)
        : await api.create(form);
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Unknown error" }));
        throw new Error(err?.error ?? "Failed to save entity.");
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["nlp-entities"] });
      toast({ title: editing ? "Entity updated" : "Entity created" });
      onClose();
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const set = (k: string, v: unknown) => setForm(f => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Entity" : "New Entity"}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Name <span className="text-destructive">*</span></Label>
              <Input value={form.name} onChange={e => set("name", e.target.value)}
                placeholder="e.g. programName" />
            </div>
            <div className="space-y-1">
              <Label>Domain <span className="text-destructive">*</span></Label>
              <Input value={form.domain} onChange={e => set("domain", e.target.value)}
                placeholder="e.g. Admissions" />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Description</Label>
            <Textarea rows={2} value={form.description}
              onChange={e => set("description", e.target.value)}
              placeholder="What information does this entity capture?" />
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={form.isActive} onCheckedChange={v => set("isActive", v)} />
            <Label>Active</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : editing ? "Save changes" : "Create entity"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── pattern panel ────────────────────────────────────────────────────────────

function PatternPanel({ entity }: { entity: NlpEntity }) {
  const qc = useQueryClient();
  const [pattern, setPattern]   = useState("");
  const [pType,   setPType]     = useState<"keyword" | "regex">("keyword");

  const add = useMutation({
    mutationFn: () => api.addPattern(entity.id, { pattern: pattern.trim(), patternType: pType }),
    onSuccess: async (res) => {
      if (!res.ok) return;
      qc.invalidateQueries({ queryKey: ["nlp-entities"] });
      setPattern("");
    },
  });

  const remove = useMutation({
    mutationFn: (pId: string) => api.removePattern(entity.id, pId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["nlp-entities"] }),
  });

  return (
    <div className="space-y-2">
      {entity.patterns.length === 0 && (
        <p className="text-xs text-muted-foreground">No patterns yet — add a keyword or regex below.</p>
      )}
      {entity.patterns.map(p => (
        <div key={p.id}
          className="flex items-start justify-between rounded border bg-muted/40 px-2 py-1.5 gap-2">
          <div className="min-w-0">
            <Badge variant="outline" className="text-[10px] mr-1.5">{p.patternType}</Badge>
            <code className="text-xs break-all">{p.pattern}</code>
          </div>
          <button className="shrink-0 hover:text-destructive mt-0.5" onClick={() => remove.mutate(p.id)}>
            <X className="h-3 w-3" />
          </button>
        </div>
      ))}

      <div className="flex gap-2 items-center">
        <Select value={pType} onValueChange={v => setPType(v as "keyword" | "regex")}>
          <SelectTrigger className="h-7 w-24 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="keyword">keyword</SelectItem>
            <SelectItem value="regex">regex</SelectItem>
          </SelectContent>
        </Select>
        <Input className="h-7 text-xs font-mono" placeholder={pType === "regex" ? "\\b(word)\\b" : "keyword phrase"}
          value={pattern} onChange={e => setPattern(e.target.value)}
          onKeyDown={e => e.key === "Enter" && pattern.trim() && add.mutate()} />
        <Button size="sm" className="h-7 px-2" onClick={() => add.mutate()}
          disabled={!pattern.trim() || add.isPending}>
          <Plus className="h-3 w-3" />
        </Button>
      </div>

      {pType === "regex" && (
        <p className="text-[10px] text-muted-foreground">
          Use capture group <code className="bg-muted px-0.5">(…)</code> to extract the matched substring.
        </p>
      )}
    </div>
  );
}

// ─── entity card ──────────────────────────────────────────────────────────────

function EntityCard({ entity }: { entity: NlpEntity }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [expanded, setExpanded] = useState(false);
  const [editing,  setEditing]  = useState(false);

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(entity.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["nlp-entities"] });
      toast({ title: "Entity deleted" });
    },
  });

  return (
    <Card>
      <CardHeader className="py-3 px-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => setExpanded(e => !e)}
              aria-expanded={expanded}
              aria-label={`${expanded ? "Collapse" : "Expand"} entity ${entity.name}`}
            >
              {expanded ? <ChevronDown className="h-4 w-4 shrink-0" aria-hidden="true" /> : <ChevronRight className="h-4 w-4 shrink-0" aria-hidden="true" />}
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm font-semibold">{entity.name}</span>
                <Badge variant="secondary" className="text-xs">{entity.domain}</Badge>
                {!entity.isActive && <Badge variant="outline" className="text-xs text-muted-foreground">inactive</Badge>}
              </div>
              {entity.description && (
                <p className="text-xs text-muted-foreground mt-0.5 truncate">{entity.description}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <span className="text-xs text-muted-foreground hidden sm:inline">
              {entity.patterns.length} pattern{entity.patterns.length !== 1 ? "s" : ""}
            </span>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(true)} aria-label={`Edit entity ${entity.name}`}>
              <Edit2 className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:bg-destructive/10"
              onClick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending}
              aria-label={`Delete entity ${entity.name}`}>
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0 pb-4 px-4">
          <PatternPanel entity={entity} />
        </CardContent>
      )}

      {editing && <EntityDialog open entity={entity} onClose={() => setEditing(false)} />}
    </Card>
  );
}

// ─── page ─────────────────────────────────────────────────────────────────────

export default function NlpEntitiesPage() {
  const [creating,   setCreating]  = useState(false);
  const [search,     setSearch]    = useState("");
  const [domFilter,  setDomFilter] = useState("");

  const { data: entities = [], isLoading } = useQuery<NlpEntity[]>({
    queryKey: ["nlp-entities"],
    queryFn:  api.list,
  });

  const domains = [...new Set(entities.map(e => e.domain))].sort();
  const filtered = entities.filter(e => {
    const q = search.toLowerCase();
    const matchSearch = !q || e.name.toLowerCase().includes(q)
      || e.domain.toLowerCase().includes(q)
      || e.description.toLowerCase().includes(q)
      || e.patterns.some(p => p.pattern.toLowerCase().includes(q));
    const matchDomain = !domFilter || e.domain === domFilter;
    return matchSearch && matchDomain;
  });

  const grouped = filtered.reduce<Record<string, NlpEntity[]>>((acc, e) => {
    (acc[e.domain] ??= []).push(e);
    return acc;
  }, {});

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">NLP Entities</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Extractable entity types matched against user messages via keyword or regex patterns.
            Extracted values persist across conversation turns.
          </p>
        </div>
        <Button onClick={() => setCreating(true)} className="gap-1.5">
          <Plus className="h-4 w-4" /> New Entity
        </Button>
      </div>

      <div className="flex gap-2 flex-wrap">
        <Input className="w-60" placeholder="Search entities…" value={search}
          onChange={e => setSearch(e.target.value)} />
        <div className="flex gap-1 flex-wrap">
          <Button size="sm" variant={domFilter === "" ? "default" : "outline"}
            onClick={() => setDomFilter("")}>All</Button>
          {domains.map(d => (
            <Button key={d} size="sm" variant={domFilter === d ? "default" : "outline"}
              onClick={() => setDomFilter(d)}>{d}</Button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground text-sm">Loading entities…</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed p-10 text-center text-muted-foreground">
          {entities.length === 0
            ? "No entities yet — click \"New Entity\" to create one."
            : "No entities match your search."}
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([domain, items]) => (
            <div key={domain} className="space-y-2">
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{domain}</h2>
              <div className="space-y-2">
                {items.map(entity => <EntityCard key={entity.id} entity={entity} />)}
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && <EntityDialog open onClose={() => setCreating(false)} />}
    </div>
  );
}
